import {
  Access,
  AgsRoleToDmsProcessMap,
  Node,
  Prisma,
  Process,
  ProcessPdmPrefix,
  ProcessThirdPartyIp,
  User,
} from '@prisma/client';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { CreateProcessDto } from '@app/domain-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

export const processKeys = {
  all: ['processes'] as const,
  lists: () => [...processKeys.all, 'list'] as const,
  list: (filters: object) => [...processKeys.lists(), filters] as const,
  items: () => [...processKeys.all, 'item'] as const,
  item: (id: number | object) => [...processKeys.items(), id] as const,
};

const PROCESS_URL = 'processes';

export type ProcessWithDetails = Process & {
  agsRoleToDmsProcessMap: (AgsRoleToDmsProcessMap & { access: Access })[];
  pdmPrefixes: (ProcessPdmPrefix & { thirdPartyIp: ProcessThirdPartyIp[] })[];
  thirdPartyIps: ProcessThirdPartyIp[];
  t3Node?: Node;
  createdByUser: User;
  updatedByUser: User;
};

export const useCreateProcess = () => {
  const axios = Axios.useAxiosApi();

  return useMutation({
    mutationFn: (data: CreateProcessDto) => axios.post<ProcessWithDetails>(`/${PROCESS_URL}`, data),
  });
};

export const useDeleteProcess = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<ProcessWithDetails>, AxiosError, number>({
    mutationFn: (id?: number | string) => axios.delete<ProcessWithDetails>(`/${PROCESS_URL}/${id}`),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: processKeys.lists() });
      queryClient.removeQueries({ queryKey: processKeys.item(Number(data.data.id)) });
    },
  });
};

export const useEditProcess = (id?: number | string) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProcessDto) => axios.patch<ProcessWithDetails>(`/${PROCESS_URL}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: processKeys.lists() });
      queryClient.invalidateQueries({ queryKey: processKeys.item(Number(id)) });
    },
  });
};

export const useFindProcesses = <T>(
  args: Prisma.ProcessFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: processKeys.list(args),
    queryFn: async () => {
      const { data } = await axios.post(`/${PROCESS_URL}/find`, args);
      return data;
    },
  });
};

export const useFindUniqueProcess = <T>(
  args: Prisma.ProcessFindUniqueArgs,
  options?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<T>({
    ...options,
    queryKey: processKeys.item(args),
    queryFn: async () => {
      const { data } = await axios.post(`/${PROCESS_URL}/find-unique`, args);
      return data;
    },
  });
};

export const useGetProcess = (
  id: number,
  options?: Omit<UseQueryOptions<ProcessWithDetails>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<ProcessWithDetails>({
    ...options,
    queryKey: processKeys.item(id),
    queryFn: async () => {
      const { data } = await axios.get(`/${PROCESS_URL}/${id}`);
      return data;
    },
  });
};

export const useFindT3Nodes = <T>(
  args?: Prisma.ProcessFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: processKeys.list(args ?? {}),
    queryFn: async () => {
      const { data } = await axios.post(`/node/find`, args);
      return data;
    },
  });
};
